import { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const cache = new Map();

/**
 * Fetch and cache an employer's white-label branding payload.
 * Returns `{ brand, loading }` where `brand` may be `null` while loading.
 * Safe to call with `null` employerId — it will simply stay in loading state.
 */
export function useEmployerBrand(employerId) {
  const [brand, setBrand] = useState(() =>
    employerId && cache.has(employerId) ? cache.get(employerId) : null
  );
  const [loading, setLoading] = useState(!brand && !!employerId);

  useEffect(() => {
    if (!employerId) return;
    if (cache.has(employerId)) {
      setBrand(cache.get(employerId));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_BASE}/api/public/employer-brand/${employerId}`)
      .then(({ data }) => {
        if (cancelled) return;
        cache.set(employerId, data);
        setBrand(data);
      })
      .catch(() => {
        if (cancelled) return;
        // 404 → no employer / brand → render Hireloop default chrome
        const fallback = {
          company: "",
          logo_url: null,
          hide_powered_by: false,
        };
        cache.set(employerId, fallback);
        setBrand(fallback);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [employerId]);

  return { brand, loading };
}

export function clearBrandCache(employerId) {
  if (employerId) cache.delete(employerId);
  else cache.clear();
}

/**
 * Logo image with sensible fallback. Pass a `brand` object from
 * `useEmployerBrand` or any payload that exposes `logo_url` + `company`.
 */
export function BrandLogo({ brand, className = "h-8 w-auto", alt }) {
  if (!brand?.logo_url) return null;
  return (
    <img
      data-testid="brand-logo"
      src={`${API_BASE}${brand.logo_url}`}
      alt={alt || brand.company || "Company logo"}
      className={className}
    />
  );
}

/**
 * "Powered by Hireloop PRO" attribution rendered at the bottom of
 * candidate-facing pages. Hidden when `brand.hide_powered_by` is true.
 */
export function PoweredByBadge({ brand, className = "" }) {
  if (brand?.hide_powered_by) return null;
  return (
    <p
      data-testid="powered-by-hireloop"
      className={`text-[10px] uppercase tracking-[0.18em] text-slate-400 ${className}`}
    >
      Powered by Hireloop PRO
    </p>
  );
}
